import React, { memo, useMemo, useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, Image, Platform, type TextStyle, type ViewStyle } from 'react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import moment from 'moment';
import { SvgIcon } from '../../components/common/SvgIcon';
import { Clickable2 } from '../../components/common/Clickable2';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import {
  createCompactHeroCtaLabelStyle,
  createCompactHeroCtaSurfaceStyle,
} from '../../shared/theme/sharedStyles';
import { formatCurrency } from '../../shared/utils/currencyUtils';
import { getApiText, getLocale, getSubscriptionPlanTitleFromMerged, useTranslation, formatNumber, layoutLtrStyle } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import {
  useSubscriptionPlans,
  filterFreezeToggleableDates,
  findCatalogPlanForSubscription,
  getCatalogPlanPortion,
  resolvePurchasedSubscriptionCardNutrition,
  resolveSubscriptionCardNutritionDisplay,
  formatPersonCountLabel,
  canUpgradeSubscription,
} from '../../data/domains/subscription';
import type { SubscriptionData } from '../../data/domains/subscription';
import { resolveMergedSubscriptionImageUri } from '../../data/domains/subscription';
import {
  useOrganizationSettings,
  useShowSubscriptionScaledMacrosEnabled,
  useCaloriesLimitVisible,
  usePortionTextMode,
  useShowPortionEnabled,
} from '../../data/domains/config';
import {
  DASH_STUDIO_RADIUS,
  androidFlatSurfaceStyle,
  androidScaleFont,
  androidScaleSize,
  studioCardShell,
} from '../../shared/theme/studio/stackScreenShell';
import { getFreezeActionColors, getUnfreezeActionColors } from '../../calendar/components/calendarDateCellVisuals';

const THUMB = androidScaleSize(56);
const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

function buildCalorieRangeLabel(
  display: ReturnType<typeof resolveSubscriptionCardNutritionDisplay>,
): string | null {
  if (!display.show_calories_limit) {
    return null;
  }
  const min = display.fields.min_calories != null ? formatNumber(Math.round(display.fields.min_calories)) : null;
  const max = display.fields.max_calories != null ? formatNumber(Math.round(display.fields.max_calories)) : null;
  if (min != null && max != null) {
    return `${min} – ${max} kcal`;
  }
  if (min != null) {
    return `${min} kcal`;
  }
  if (max != null) {
    return `${max} kcal`;
  }
  return null;
}

function buildScaledOrPortionLine(
  display: ReturnType<typeof resolveSubscriptionCardNutritionDisplay>,
  t: (key: string, params?: Record<string, string | number>) => string,
  portion_text_mode: 'portion' | 'person',
): string | null {
  if (display.show_portion && display.fields.portion != null) {
    return formatPersonCountLabel(display.fields.portion, t, portion_text_mode);
  }
  if (!display.show_scaled_macros) {
    return null;
  }
  const parts: string[] = [];
  if (display.fields.scaled_protein != null) {
    parts.push(`${formatNumber(Math.round(display.fields.scaled_protein))}g P`);
  }
  if (display.fields.scaled_carbs != null) {
    parts.push(`${formatNumber(Math.round(display.fields.scaled_carbs))}g C`);
  }
  return parts.length > 0 ? parts.join('  ·  ') : null;
}

export type SubscriptionDisplayStatus = 'active' | 'pending' | 'paused' | 'expired' | 'cancelled' | 'other';

export function resolveSubscriptionDisplayStatus(
  status: string | undefined,
  payment_status?: string | undefined,
): SubscriptionDisplayStatus {
  const normalized = String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  const payment_normalized = String(payment_status ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (['activated', 'active'].includes(normalized)) {
    return 'active';
  }
  if (
    ['pending', 'awaiting', 'awaiting_payment', 'unpaid', 'payment_pending'].includes(normalized) ||
    payment_normalized === 'pending'
  ) {
    return 'pending';
  }
  if (['paused', 'frozen', 'on_hold'].includes(normalized)) {
    return 'paused';
  }
  if (['expired', 'completed', 'ended', 'finished'].includes(normalized)) {
    return 'expired';
  }
  if (['cancelled', 'canceled', 'cancel'].includes(normalized)) {
    return 'cancelled';
  }
  return 'other';
}

function createStyles(c: ThemeColors, text_align: 'left' | 'right') {
  const sf = androidScaleFont;
  const ss = androidScaleSize;

  return StyleSheet.create({
    wrap: { marginBottom: appTheme.spacing.sm, width: '100%' },
    card: {
      width: '100%',
      overflow: 'visible',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      shadowColor: c.shadowColor,
    },
    cardMuted: { opacity: 0.88 },
    body: {
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: ss(12),
      gap: ss(10),
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: ss(10),
    },
    thumbWrap: {
      width: THUMB,
      height: THUMB,
      borderRadius: ss(14),
      overflow: 'hidden',
      flexShrink: 0,
      backgroundColor: c.surfaceElevated,
    },
    thumbImage: { width: '100%', height: '100%' },
    thumbFallback: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.primaryUltra ?? hexToRgba(c.primary, 0.12),
    },
    topCopy: { flex: 1, minWidth: 0, gap: ss(3) },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: ss(6),
    },
    title: {
      flex: 1,
      minWidth: 0,
      fontSize: sf(14),
      fontWeight: '800',
      color: c.text,
      letterSpacing: -0.15,
      lineHeight: sf(18),
      textAlign: text_align,
      ...ANDROID_TEXT_COMPACT,
    },
    statusBadge: {
      flexShrink: 0,
      paddingHorizontal: ss(7),
      paddingVertical: ss(2),
      borderRadius: appTheme.borderRadius.pill,
      borderWidth: StyleSheet.hairlineWidth,
      maxWidth: '40%',
    },
    statusBadgeText: {
      fontSize: sf(9),
      fontWeight: '800',
      letterSpacing: 0.2,
      textTransform: 'uppercase',
      ...ANDROID_TEXT_COMPACT,
    },
    metaLine: {
      fontSize: sf(11),
      lineHeight: sf(14),
      fontWeight: '600',
      color: c.textSecondary,
      textAlign: text_align,
      ...ANDROID_TEXT_COMPACT,
    },
    infoRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      justifyContent: 'space-between',
      gap: ss(10),
    },
    nutritionCol: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'center',
      gap: ss(2),
    },
    nutritionScaled: {
      fontSize: sf(12),
      fontWeight: '700',
      color: c.textLight,
      letterSpacing: -0.1,
      lineHeight: sf(16),
      textAlign: text_align,
      ...ANDROID_TEXT_COMPACT,
    },
    nutritionCalories: {
      fontSize: sf(11),
      fontWeight: '600',
      color: c.textSecondary,
      lineHeight: sf(14),
      textAlign: text_align,
      ...ANDROID_TEXT_COMPACT,
    },
    tagsCol: {
      flexShrink: 0,
      maxWidth: '52%',
      justifyContent: 'center',
      gap: ss(4),
    },
    tagsCluster: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: ss(4),
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
    metaTag: {
      paddingHorizontal: ss(8),
      paddingVertical: ss(3),
      borderRadius: appTheme.borderRadius.pill,
      borderWidth: StyleSheet.hairlineWidth,
      maxWidth: '100%',
    },
    metaTagFlex: {
      flexShrink: 1,
    },
    metaTagText: {
      fontSize: sf(10),
      fontWeight: '800',
      letterSpacing: 0.1,
      ...ANDROID_TEXT_COMPACT,
    },
    plainMeta: {
      flex: 1,
      minWidth: 0,
      fontSize: sf(12),
      fontWeight: '600',
      color: c.textSecondary,
      lineHeight: sf(16),
      textAlign: text_align,
      ...ANDROID_TEXT_COMPACT,
    },
    pendingBanner: {
      paddingHorizontal: ss(8),
      paddingVertical: ss(5),
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: hexToRgba(c.warning, 0.1),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.warning, 0.28),
    },
    pendingBannerText: {
      fontSize: sf(10),
      fontWeight: '600',
      color: c.warningText ?? c.warning,
      lineHeight: sf(14),
      textAlign: text_align,
      ...ANDROID_TEXT_COMPACT,
    },
    metricsBlock: {
      gap: ss(5),
    },
    metricsTop: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: appTheme.spacing.sm,
    },
    daysInline: {
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: ss(4),
      flexShrink: 0,
    },
    daysValue: {
      fontSize: sf(15),
      fontWeight: '800',
      color: c.primary,
      lineHeight: sf(18),
      fontVariant: ['tabular-nums'],
      ...ANDROID_TEXT_COMPACT,
    },
    daysCaption: {
      fontSize: sf(11),
      fontWeight: '600',
      color: c.textSecondary,
      ...ANDROID_TEXT_COMPACT,
    },
    progressCount: {
      flexShrink: 0,
      fontSize: sf(11),
      fontWeight: '700',
      color: c.textSecondary,
      fontVariant: ['tabular-nums'],
      ...ANDROID_TEXT_COMPACT,
    },
    progressTrack: {
      height: ss(5),
      borderRadius: 100,
      backgroundColor: hexToRgba(c.text, 0.07),
      overflow: 'hidden',
      ...layoutLtrStyle,
    },
    progressFill: {
      height: '100%',
      borderRadius: 100,
      minWidth: ss(2),
      backgroundColor: c.primary,
    },
    metricsFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.sm,
    },
    priceBlock: {
      flexShrink: 0,
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: ss(4),
    },
    priceOriginal: {
      fontSize: sf(10),
      color: c.textLight,
      textDecorationLine: 'line-through',
      ...ANDROID_TEXT_COMPACT,
    },
    priceCurrent: {
      fontSize: sf(13),
      fontWeight: '800',
      color: c.text,
      letterSpacing: -0.15,
      ...ANDROID_TEXT_COMPACT,
    },
    footerMeta: {
      flex: 1,
      minWidth: 0,
      fontSize: sf(10),
      fontWeight: '600',
      color: c.textLight,
      textAlign: text_align,
      ...ANDROID_TEXT_COMPACT,
    },
    actions: { gap: ss(6) },
    actionRow: {
      flexDirection: 'row',
      gap: ss(6),
      alignItems: 'center',
    },
    actionSlot: { flex: 1, minWidth: 0 },
    actionSlotFull: { width: '100%' },
    actionDisabled: { opacity: 0.4 },
    actionSurface: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: ss(7),
      paddingHorizontal: ss(8),
      borderRadius: 999,
      borderWidth: 1,
      ...Platform.select({
        ios: {
          shadowColor: 'transparent',
          shadowOpacity: 0,
          shadowRadius: 0,
          shadowOffset: { width: 0, height: 0 },
        },
        android: androidFlatSurfaceStyle(),
      }),
    } satisfies ViewStyle,
    actionLabel: {
      fontSize: sf(10),
      fontWeight: '800',
      letterSpacing: 0.35,
      textAlign: 'center',
      ...ANDROID_TEXT_COMPACT,
    },
  });
}

/** Colors only — never layout/minHeight from compact hero CTAs (those inflate Renew vs Freeze). */
function compactCtaColors(
  c: ThemeColors,
  variant: 'tintAlert' | 'tintSuccess' | 'tintPrimary' | 'solidPrimary',
): {
  surface: ViewStyle;
  label: TextStyle;
} {
  const surface = createCompactHeroCtaSurfaceStyle(c, variant);
  const label = createCompactHeroCtaLabelStyle(c, variant);
  return {
    surface: {
      backgroundColor: surface.backgroundColor,
      borderColor: surface.borderColor,
    },
    label: {
      color: label.color,
      fontWeight: '800',
    },
  };
}

export type MySubscriptionCardProps = {
  subscription: SubscriptionData;
  index: number;
  days_left: number;
  paused_days_left?: number;
  days_elapsed: number;
  total_days: number;
  progress_percentage: number;
  onFreeze: () => void;
  onUnfreeze: () => void;
  onRenew: () => void;
  onUpgrade?: () => void;
  onPayPending?: () => void;
  onCancelPending?: () => void;
  pending_action_loading?: boolean;
  themeColors: ThemeColors;
};

export const MySubscriptionCard = memo(function MySubscriptionCard({
  subscription,
  index,
  days_left,
  paused_days_left = 0,
  days_elapsed,
  total_days,
  progress_percentage,
  onFreeze,
  onUnfreeze,
  onRenew,
  onUpgrade,
  onPayPending,
  onCancelPending,
  pending_action_loading = false,
  themeColors,
}: MySubscriptionCardProps) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const { settings } = useOrganizationSettings();
  const menu_selection_before = settings?.menu_selection_before ?? 3;
  const { data: catalog_plans } = useSubscriptionPlans();
  const show_scaled_macros = useShowSubscriptionScaledMacrosEnabled();
  const show_calories_limit = useCaloriesLimitVisible();
  const show_portion = useShowPortionEnabled();
  const portion_text_mode = usePortionTextMode();
  const text_align = rtl.textAlign;
  const writing = rtl.writingDirection;
  const styles = useMemo(() => createStyles(themeColors, text_align), [themeColors, text_align]);
  const [image_failed, set_image_failed] = useState(false);

  const payment_status = (subscription as { payment_status?: string }).payment_status;
  const display_status = useMemo(
    () => resolveSubscriptionDisplayStatus(subscription.status, payment_status),
    [subscription.status, payment_status],
  );

  const card_model = useMemo(() => {
    const detail = subscription.detail ?? {};
    const detail_record = detail as Record<string, unknown>;
    const sub_record = subscription as SubscriptionData & {
      final_cost?: number;
      original_cost?: number;
      payment_mode?: string;
      subscription_name?: string;
    };

    const start = moment(subscription.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
    const end = moment(subscription.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
    const locale = getLocale();
    const date_locale = locale === 'ar' ? 'ar' : 'en';

    const title =
      getSubscriptionPlanTitleFromMerged(subscription, t('account.activePlan')) ||
      getApiText(detail_record, 'title', 'a_title') ||
      getApiText(detail_record, 'title', 'arabic_title') ||
      getApiText(detail_record, 'name', 'arabic_title') ||
      sub_record.subscription_name ||
      t('account.activePlan');

    const cost = Number(sub_record.final_cost ?? subscription.cost ?? detail.cost ?? detail_record.price ?? 0);
    const original_cost = Number(sub_record.original_cost ?? subscription.cost ?? cost);
    const has_discount = original_cost > cost && original_cost > 0;

    const allowed_freeze = Number(detail.allowed_freeze_days ?? (sub_record as { allowed_freeze_days?: number }).allowed_freeze_days ?? 0);
    const used_freeze = Number(subscription.used_freeze_count ?? 0);
    const remaining_freeze = Math.max(0, allowed_freeze - used_freeze);
    const frozen_days = subscription.freeze_dates?.length ?? 0;

    const nutrition_fields = resolvePurchasedSubscriptionCardNutrition(subscription);
    if (nutrition_fields.portion == null && catalog_plans?.length) {
      const plan = findCatalogPlanForSubscription(subscription, catalog_plans);
      const catalog_portion = plan ? getCatalogPlanPortion(plan as Record<string, unknown>) : null;
      if (catalog_portion != null) {
        nutrition_fields.portion = catalog_portion;
      }
    }
    const nutrition_display = resolveSubscriptionCardNutritionDisplay(nutrition_fields, {
      show_scaled_macros,
      calories_limit: show_calories_limit,
      show_portion,
    });

    return {
      title,
      image_uri: resolveMergedSubscriptionImageUri(subscription, catalog_plans),
      start_str: start.isValid() ? start.locale(date_locale).format('ll') : '—',
      end_str: end.isValid() ? end.locale(date_locale).format('ll') : '—',
      meals: Number(detail.total_meals ?? 0),
      snacks: Number(detail.total_snacks ?? 0),
      price: cost,
      original_price: original_cost,
      has_discount,
      remaining_freeze,
      frozen_days,
      nutrition_display,
    };
  }, [subscription, t, catalog_plans, show_scaled_macros, show_calories_limit, show_portion]);

  useEffect(() => {
    set_image_failed(false);
  }, [card_model.image_uri, subscription.id]);

  const status_label = useMemo(() => {
    switch (display_status) {
      case 'active':
        return t('subscriptions.statusActive');
      case 'pending':
        return t('subscriptions.statusPending');
      case 'paused':
        return t('subscriptions.statusPaused');
      case 'expired':
        return t('subscriptions.statusExpired');
      case 'cancelled':
        return t('subscriptions.statusCancelled');
      default:
        return t('subscriptions.statusOther');
    }
  }, [display_status, t]);

  const cta = useMemo(() => {
    const freeze = getFreezeActionColors(themeColors);
    const unfreeze = getUnfreezeActionColors(themeColors);
    const upgrade = compactCtaColors(themeColors, 'tintPrimary');
    const renew = compactCtaColors(themeColors, 'tintAlert');
    const pay = compactCtaColors(themeColors, 'solidPrimary');
    const cancel = compactCtaColors(themeColors, 'tintAlert');
    return {
      freezeS: { backgroundColor: freeze.background, borderColor: freeze.border },
      freezeL: { color: freeze.foreground, fontWeight: '800' as const },
      unfreezeS: { backgroundColor: unfreeze.background, borderColor: unfreeze.border },
      unfreezeL: { color: unfreeze.foreground, fontWeight: '800' as const },
      upgradeS: upgrade.surface,
      upgradeL: upgrade.label,
      renewS: renew.surface,
      renewL: renew.label,
      payS: pay.surface,
      payL: pay.label,
      cancelS: cancel.surface,
      cancelL: cancel.label,
    };
  }, [themeColors]);

  const is_lifecycle_inactive = display_status === 'expired' || display_status === 'cancelled';
  const can_freeze = display_status === 'active' && card_model.remaining_freeze > 0;
  const unlockable_frozen_count = useMemo(
    () => filterFreezeToggleableDates(subscription.freeze_dates ?? [], menu_selection_before).length,
    [subscription.freeze_dates, menu_selection_before],
  );
  const can_unfreeze =
    (display_status === 'active' || display_status === 'paused') && unlockable_frozen_count > 0;
  const can_upgrade =
    (display_status === 'active' || display_status === 'paused') &&
    onUpgrade != null &&
    canUpgradeSubscription(subscription);
  const show_progress = display_status === 'active' || display_status === 'paused';
  const show_pending_actions = display_status === 'pending' && onPayPending != null && onCancelPending != null;
  const show_active_actions = display_status === 'active' || display_status === 'paused';
  const show_freeze_action = display_status === 'active' || display_status === 'paused';

  const scaled_or_portion_line = buildScaledOrPortionLine(card_model.nutrition_display, t, portion_text_mode);
  const calorie_range_label = buildCalorieRangeLabel(card_model.nutrition_display);
  const meals_label = card_model.meals > 0 ? `${formatNumber(card_model.meals)} ${t('dashboard.mealsLabel')}` : null;
  const snacks_label = card_model.snacks > 0 ? `${formatNumber(card_model.snacks)} ${t('dashboard.snacksLabel')}` : null;
  const show_nutrition = scaled_or_portion_line != null || calorie_range_label != null;
  const plain_meal_snack_line = [meals_label, snacks_label].filter(Boolean).join(' · ');
  const show_info_row = show_nutrition || plain_meal_snack_line.length > 0;

  const footer_meta_parts = [
    display_status === 'active'
      ? card_model.remaining_freeze > 0
        ? t('subscriptions.pausesRemaining', { count: card_model.remaining_freeze })
        : t('subscriptions.noPausesRemaining')
      : null,
    card_model.frozen_days > 0 ? t('subscriptions.frozenDaysCount', { count: card_model.frozen_days }) : null,
  ].filter(Boolean);

  const days_caption =
    paused_days_left > 0
      ? t('calendar.deliveryDaysHeroCaptionPaused', { paused: paused_days_left })
      : days_left === 1
        ? t('dashboard.dayLeft')
        : t('dashboard.daysLeft');

  const render_action = useCallback(
    (
      label: string,
      on_press: () => void,
      surface_style: ViewStyle,
      label_style: TextStyle,
      disabled?: boolean,
      full_width?: boolean,
    ) => (
      <View style={full_width ? styles.actionSlotFull : styles.actionSlot}>
        <Clickable2
          onPress={on_press}
          disabled={disabled}
          style={{ width: '100%' }}
          accessibilityLabel={label}
          accessibilityRole="button"
          haptics>
          <View style={[styles.actionSurface, surface_style, disabled && styles.actionDisabled]}>
            <Text style={[styles.actionLabel, label_style]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>
              {label}
            </Text>
          </View>
        </Clickable2>
      </View>
    ),
    [styles],
  );

  return (
    <Animated.View entering={FadeInDown.delay(index * 50).springify().damping(58)} style={styles.wrap}>
      <View style={[styles.card, is_lifecycle_inactive && styles.cardMuted]}>
        <View style={styles.body}>
          <View style={styles.topRow}>
            <View style={styles.thumbWrap}>
              {card_model.image_uri && !image_failed ? (
                <Image
                  source={{ uri: card_model.image_uri }}
                  style={styles.thumbImage}
                  resizeMode="cover"
                  fadeDuration={0}
                  onError={() => set_image_failed(true)}
                />
              ) : (
                <View style={styles.thumbFallback}>
                  <SvgIcon name="meal" size={androidScaleSize(22)} color={themeColors.primary} />
                </View>
              )}
            </View>

            <View style={styles.topCopy}>
              <View style={styles.titleRow}>
                <Text style={[styles.title, { writingDirection: writing }]} numberOfLines={1}>
                  {card_model.title}
                </Text>
                <View
                  style={[
                    styles.statusBadge,
                    {
                      backgroundColor: hexToRgba(themeColors.primary, 0.1),
                      borderColor: hexToRgba(themeColors.primary, 0.22),
                    },
                  ]}>
                  <Text
                    style={[styles.statusBadgeText, { color: themeColors.primary, writingDirection: writing }]}
                    numberOfLines={1}>
                    {status_label}
                  </Text>
                </View>
              </View>
              <Text style={[styles.metaLine, { writingDirection: writing }]} numberOfLines={1}>
                {`${card_model.start_str} – ${card_model.end_str}`}
              </Text>
            </View>
          </View>

          {show_info_row ? (
            <View style={[styles.infoRow, rtl.flipRowStyle]}>
              {show_nutrition ? (
                <>
                  <View style={styles.nutritionCol}>
                    {scaled_or_portion_line != null ? (
                      <Text style={[styles.nutritionScaled, { writingDirection: writing }]} numberOfLines={1}>
                        {scaled_or_portion_line}
                      </Text>
                    ) : null}
                    {calorie_range_label != null ? (
                      <Text style={[styles.nutritionCalories, { writingDirection: writing }]} numberOfLines={1}>
                        {calorie_range_label}
                      </Text>
                    ) : null}
                  </View>
                  {(meals_label || snacks_label) ? (
                    <View style={styles.tagsCol}>
                      <View style={[styles.tagsCluster, rtl.flipRowStyle]}>
                        {meals_label ? (
                          <View
                            style={[
                              styles.metaTag,
                              styles.metaTagFlex,
                              {
                                backgroundColor: themeColors.primary,
                                borderColor: themeColors.primary,
                              },
                            ]}>
                            <Text
                              style={[styles.metaTagText, { color: themeColors.primaryText, writingDirection: writing }]}
                              numberOfLines={1}>
                              {meals_label}
                            </Text>
                          </View>
                        ) : null}
                        {snacks_label ? (
                          <View
                            style={[
                              styles.metaTag,
                              styles.metaTagFlex,
                              {
                                backgroundColor: themeColors.primaryText,
                                borderColor: themeColors.primary,
                              },
                            ]}>
                            <Text
                              style={[styles.metaTagText, { color: themeColors.primary, writingDirection: writing }]}
                              numberOfLines={1}>
                              {snacks_label}
                            </Text>
                          </View>
                        ) : null}
                      </View>
                    </View>
                  ) : null}
                </>
              ) : (
                <Text style={[styles.plainMeta, { writingDirection: writing }]} numberOfLines={1}>
                  {plain_meal_snack_line}
                </Text>
              )}
            </View>
          ) : null}

          {display_status === 'pending' ? (
            <View style={styles.pendingBanner}>
              <Text style={[styles.pendingBannerText, { writingDirection: writing }]}>{t('subscriptions.pendingPaymentHint')}</Text>
            </View>
          ) : null}

          {show_progress ? (
            <View style={styles.metricsBlock}>
              <View style={styles.metricsTop}>
                <View style={styles.daysInline}>
                  <Text style={[styles.daysValue, { writingDirection: writing }]}>{formatNumber(Math.max(0, days_left))}</Text>
                  <Text style={[styles.daysCaption, { writingDirection: writing }]} numberOfLines={1}>
                    {days_caption}
                  </Text>
                </View>
                <Text style={[styles.progressCount, { writingDirection: writing }]}>
                  {formatNumber(days_elapsed)}/{formatNumber(Math.max(1, total_days))}
                </Text>
              </View>
              <View style={styles.progressTrack} accessibilityRole="progressbar">
                <View
                  style={[
                    styles.progressFill,
                    { width: `${Math.min(100, Math.max(0, progress_percentage))}%` },
                  ]}
                />
              </View>
              {(card_model.price > 0 || footer_meta_parts.length > 0) && (
                <View style={styles.metricsFooter}>
                  {card_model.price > 0 ? (
                    <View style={styles.priceBlock}>
                      {card_model.has_discount ? (
                        <Text style={styles.priceOriginal}>{formatCurrency(card_model.original_price)}</Text>
                      ) : null}
                      <Text style={styles.priceCurrent}>{formatCurrency(card_model.price)}</Text>
                    </View>
                  ) : (
                    <View />
                  )}
                  {footer_meta_parts.length > 0 ? (
                    <Text style={[styles.footerMeta, { writingDirection: writing }]} numberOfLines={1}>
                      {footer_meta_parts.join(' · ')}
                    </Text>
                  ) : null}
                </View>
              )}
            </View>
          ) : card_model.price > 0 || footer_meta_parts.length > 0 ? (
            <View style={styles.metricsFooter}>
              {card_model.price > 0 ? (
                <View style={styles.priceBlock}>
                  {card_model.has_discount ? (
                    <Text style={styles.priceOriginal}>{formatCurrency(card_model.original_price)}</Text>
                  ) : null}
                  <Text style={styles.priceCurrent}>{formatCurrency(card_model.price)}</Text>
                </View>
              ) : (
                <View />
              )}
              {footer_meta_parts.length > 0 ? (
                <Text style={[styles.footerMeta, { writingDirection: writing }]} numberOfLines={1}>
                  {footer_meta_parts.join(' · ')}
                </Text>
              ) : null}
            </View>
          ) : null}

          <View style={styles.actions}>
            {show_pending_actions ? (
              <View style={styles.actionRow}>
                {render_action(t('order.delete'), onCancelPending!, cta.cancelS, cta.cancelL, pending_action_loading)}
                {render_action(t('checkout.payNow'), onPayPending!, cta.payS, cta.payL, pending_action_loading)}
              </View>
            ) : show_active_actions ? (
              <View style={styles.actionRow}>
                {show_freeze_action
                  ? render_action(t('subscriptions.freeze'), onFreeze, cta.freezeS, cta.freezeL, !can_freeze)
                  : null}
                {can_unfreeze
                  ? render_action(t('calendar.unfreeze'), onUnfreeze, cta.unfreezeS, cta.unfreezeL)
                  : null}
                {can_upgrade
                  ? render_action(t('subscriptions.upgrade'), onUpgrade!, cta.upgradeS, cta.upgradeL)
                  : null}
                {render_action(t('subscriptions.renew'), onRenew, cta.renewS, cta.renewL)}
              </View>
            ) : (
              render_action(
                is_lifecycle_inactive ? t('subscriptions.renew') : t('subscriptions.explorePlans'),
                onRenew,
                cta.renewS,
                cta.renewL,
                false,
                true,
              )
            )}
          </View>
        </View>
      </View>
    </Animated.View>
  );
});

export default MySubscriptionCard;
